import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useFavorites } from "../context/FavoritesContext";
import Loading from "../components/Loading";
import R2A2CompareHighlights from "../components/R2A2CompareHighlights";
import { ArrowLeft, X, Plus, GitCompare } from "lucide-react";

function Cell({ label, values }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: `160px repeat(${values.length}, 1fr)` }}>
      <div className="sat-label border-b border-[color:var(--sat-border)] py-2 pr-2">{label}</div>
      {values.map((v, i) => (
        <div key={i} className="font-mono text-sm border-b border-[color:var(--sat-border)] py-2 px-2">{v ?? "—"}</div>
      ))}
    </div>
  );
}

export default function ComparePage() {
  const { favs } = useFavorites();
  const [selected, setSelected] = useState([]); // list of rocket ids (strings)
  const [rockets, setRockets] = useState([]);
  const [loading, setLoading] = useState(false);

  const availableRockets = favs.filter(f => f.kind === "rocket");

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 4 ? prev : [...prev, id]));
  };

  useEffect(() => {
    if (selected.length === 0) { setRockets([]); return; }
    setLoading(true);
    api.post("/compare", { rocket_ids: selected })
      .then(r => setRockets(r.data.rockets || []))
      .catch(() => setRockets([]))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div data-testid="page-compare">
      <Link to="/pilot" className="sat-label inline-flex items-center gap-2 mb-4 hover:sat-cyan" data-testid="back-pilot">
        <ArrowLeft size={14} /> BACK TO PILOT
      </Link>
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan flex items-center gap-2"><GitCompare size={12} /> DIRECTIVE // COMPARE</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">COMPARE ROCKETS</h1>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">Select up to 4 archived rockets for side-by-side analysis.</p>
      </div>

      {/* Selector */}
      <section className="sat-surface p-4 mb-6" data-testid="compare-selector">
        <div className="sat-label sat-cyan mb-3">ARSENAL ({availableRockets.length})</div>
        {availableRockets.length === 0 ? (
          <div className="sat-label py-4 text-center">NO ROCKETS ARCHIVED — <Link to="/fleet" className="sat-cyan">BROWSE FLEET</Link></div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableRockets.map(f => {
              const on = selected.includes(f.ref_id);
              return (
                <button key={f.id} onClick={() => toggle(f.ref_id)}
                  data-testid={`compare-pick-${f.ref_id}`}
                  className={`sat-btn !py-2 !px-3 ${on ? "sat-btn-solid" : ""}`}>
                  {on ? <X size={11} /> : <Plus size={11} />} {f.title}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {loading && <Loading message="SYNTHESIZING COMPARISON" />}

      {!loading && rockets.length > 0 && (
        <div className="sat-surface p-4 overflow-x-auto" data-testid="compare-table">
          <div className="min-w-[600px]">
            <div className="grid" style={{ gridTemplateColumns: `160px repeat(${rockets.length}, 1fr)` }}>
              <div className="sat-label sat-cyan py-3 pr-2">VEHICLE</div>
              {rockets.map(r => (
                <div key={r.id} className="py-3 px-2">
                  <div className="w-full h-24 bg-black mb-2 overflow-hidden">
                    {r.image_url && <img src={r.image_url} alt="" className="w-full h-full object-contain"/>}
                  </div>
                  <div className="font-display font-bold text-sm">{r.full_name || r.name}</div>
                  <div className="sat-label mt-1">{r.family}</div>
                </div>
              ))}
            </div>
            <Cell label="LENGTH" values={rockets.map(r => r.length ? `${r.length} m` : null)} />
            <Cell label="DIAMETER" values={rockets.map(r => r.diameter ? `${r.diameter} m` : null)} />
            <Cell label="LAUNCH MASS" values={rockets.map(r => r.launch_mass ? `${r.launch_mass} t` : null)} />
            <Cell label="LEO CAPACITY" values={rockets.map(r => r.leo_capacity ? `${r.leo_capacity} kg` : null)} />
            <Cell label="GTO CAPACITY" values={rockets.map(r => r.gto_capacity ? `${r.gto_capacity} kg` : null)} />
            <Cell label="THRUST" values={rockets.map(r => r.to_thrust ? `${r.to_thrust} kN` : null)} />
            <Cell label="STAGES" values={rockets.map(r => r.max_stage)} />
            <Cell label="LAUNCH COST" values={rockets.map(r => r.launch_cost ? `$${Number(r.launch_cost).toLocaleString()}` : null)} />
            <Cell label="TOTAL FLIGHTS" values={rockets.map(r => r.total_launch_count)} />
            <Cell label="SUCCESS" values={rockets.map(r => r.successful_launches)} />
            <Cell label="FAILED" values={rockets.map(r => r.failed_launches)} />
            <Cell label="REUSABLE" values={rockets.map(r => r.reusable ? "YES" : "NO")} />
          </div>
        </div>
      )}

      {!loading && selected.length > 0 && (
        <R2A2CompareHighlights rocketIds={selected} />
      )}
    </div>
  );
}
